import React, { createContext, useContext, useReducer, useEffect } from "react";

// File interface
export interface FileRecord {
  id: string;
  name: string;
  originalName: string;
  size: number;
  type: string;
  url: string;
  uploadedBy: string;
  uploadedAt: string;
  category: string;
  description?: string;
  tags: string[];
  isPublic: boolean;
  downloadCount: number;
  lastAccessed?: string;
}

// File state interface
interface FileState {
  files: FileRecord[];
  uploadProgress: { [key: string]: number };
  isUploading: boolean;
  error: string | null;
}

// File actions
type FileAction =
  | { type: "SET_FILES"; payload: FileRecord[] }
  | { type: "ADD_FILE"; payload: FileRecord }
  | { type: "UPDATE_FILE"; payload: FileRecord }
  | { type: "DELETE_FILE"; payload: string }
  | {
      type: "SET_UPLOAD_PROGRESS";
      payload: { fileId: string; progress: number };
    }
  | { type: "SET_UPLOADING"; payload: boolean }
  | { type: "SET_ERROR"; payload: string | null }
  | { type: "INCREMENT_DOWNLOAD"; payload: string };

// Initial state
const initialState: FileState = {
  files: [],
  uploadProgress: {},
  isUploading: false,
  error: null,
};

// File reducer
function fileReducer(state: FileState, action: FileAction): FileState {
  switch (action.type) {
    case "SET_FILES":
      return { ...state, files: action.payload };
    case "ADD_FILE":
      return { ...state, files: [...state.files, action.payload] };
    case "UPDATE_FILE":
      return {
        ...state,
        files: state.files.map((f) =>
          f.id === action.payload.id ? action.payload : f
        ),
      };
    case "DELETE_FILE":
      return {
        ...state,
        files: state.files.filter((f) => f.id !== action.payload),
      };
    case "SET_UPLOAD_PROGRESS":
      return {
        ...state,
        uploadProgress: {
          ...state.uploadProgress,
          [action.payload.fileId]: action.payload.progress,
        },
      };
    case "SET_UPLOADING":
      return { ...state, isUploading: action.payload };
    case "SET_ERROR":
      return { ...state, error: action.payload };
    case "INCREMENT_DOWNLOAD":
      return {
        ...state,
        files: state.files.map((f) =>
          f.id === action.payload
            ? {
                ...f,
                downloadCount: f.downloadCount + 1,
                lastAccessed: new Date().toISOString(),
              }
            : f
        ),
      };
    default:
      return state;
  }
}

// File context
const FileContext = createContext<{
  state: FileState;
  uploadFile: (
    file: File,
    category: string,
    description?: string
  ) => Promise<FileRecord>;
  downloadFile: (fileId: string) => void;
  deleteFile: (fileId: string) => void;
  getFilesByCategory: (category: string) => FileRecord[];
  searchFiles: (query: string) => FileRecord[];
  clearError: () => void;
} | null>(null);

// File provider
export function FileProvider({ children }: { children: React.ReactNode }) {
  const [state, dispatch] = useReducer(fileReducer, initialState);

  // Load files from localStorage on mount
  useEffect(() => {
    const savedFiles = localStorage.getItem("syndicate-files");
    if (savedFiles) {
      try {
        const files = JSON.parse(savedFiles);
        dispatch({ type: "SET_FILES", payload: files });
      } catch (error) {
        // Error loading saved files
      }
    } else {
      // Initialize with sample files
      initializeSampleFiles();
    }
  }, []);

  // Save files to localStorage whenever state changes
  useEffect(() => {
    localStorage.setItem("syndicate-files", JSON.stringify(state.files));
  }, [state.files]);

  const initializeSampleFiles = () => {
    const sampleFiles: FileRecord[] = [
      {
        id: "1",
        name: "syndicate-bylaws-2024.pdf",
        originalName: "Syndicate Bylaws 2024.pdf",
        size: 2048576, // 2MB
        type: "application/pdf",
        url: "data:application/pdf;base64,JVBERi0xLjQKJcfsj6IKNSAwIG9iago8PAovVHlwZSAvUGFnZQovUGFyZW50IDMgMCBSCi9NZWRpYUJveCBbMCAwIDU5NSA4NDJdCi9SZXNvdXJjZXMgPDwKL0ZvbnQgPDwKL0YxIDYgMCBSCj4+Cj4+Ci9Db250ZW50cyA3IDAgUgo+PgplbmRvYmoKNiAwIG9iago8PAovVHlwZSAvRm9udAovU3VidHlwZSAvVHlwZTEKL0Jhc2VGb250IC9IZWx2ZXRpY2EKPj4KZW5kb2JqCjcgMCBvYmoKPDwKL0xlbmd0aCA0NAo+PgpzdHJlYW0KQlQKL0YxIDEyIFRmCjcyIDcyMCBUZAooU3luZGljYXRlIG9mIFBpbG90cyBCeWxhd3MpIFRqCkVUCmVuZHN0cmVhbQplbmRvYmoKMSAwIG9iago8PAovVHlwZSAvQ2F0YWxvZwovUGFnZXMgMyAwIFIKPj4KZW5kb2JqCjMgMCBvYmoKPDwKL1R5cGUgL1BhZ2VzCi9LaWRzIFs0IDAgUl0KL0NvdW50IDEKL01lZGlhQm94IFswIDAgNTk1IDg0Ml0KPj4KZW5kb2JqCjIgMCBvYmoKPDwKL1R5cGUgL01ldGFkYXRhCi9UeXBlIC9YTUwKL0xlbmd0aCA0NQo+PgpzdHJlYW0KPD94bWwgdmVyc2lvbj0iMS4wIiBlbmNvZGluZz0iVVRGLTgiPz4KPC9tZXRhZGF0YT4KZW5kc3RyZWFtCmVuZG9iago0IDAgb2JqCjw8Ci9UeXBlIC9QYWdlCi9QYXJlbnQgMyAwIFIKL01lZGlhQm94IFswIDAgNTk1IDg0Ml0KL1Jlc291cmNlcyA8PAovRm9udCA8PAovRjEgNiAwIFIKPj4KPj4KL0NvbnRlbnRzIDcgMCBSCj4+CmVuZG9iagp4cmVmCjAgOAowMDAwMDAwMDAwIDY1NTM1IGYKMDAwMDAwMDAwOSAwMDAwMCBuCjAwMDAwMDAwNTggMDAwMDAgbgowMDAwMDAwMTE1IDAwMDAwIG4KMDAwMDAwMDI2MiAwMDAwMCBuCjAwMDAwMDAzODEgMDAwMDAgbgowMDAwMDAwNDQwIDAwMDAwIG4KMDAwMDAwMDQ5OCAwMDAwMCBuCnRyYWlsZXIKPDwKL1NpemUgOAovUm9vdCAxIDAgUgo+PgpzdGFydHhyZWYKNTk1CiUlRU9G",
        uploadedBy: "admin@syndicate.eg",
        uploadedAt: "2024-01-15T10:00:00Z",
        category: "governance",
        description:
          "Official bylaws and regulations of the Syndicate of Pilots",
        tags: ["bylaws", "governance", "regulations"],
        isPublic: true,
        downloadCount: 15,
        lastAccessed: "2024-01-20T14:30:00Z",
      },
      {
        id: "2",
        name: "member-application-form.docx",
        originalName: "Member Application Form.docx",
        size: 512000, // 512KB
        type: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
        url: "data:application/vnd.openxmlformats-officedocument.wordprocessingml.document;base64,UEsDBBQAAAAIAA==",
        uploadedBy: "admin@syndicate.eg",
        uploadedAt: "2024-01-10T09:00:00Z",
        category: "membership",
        description: "Application form for new members",
        tags: ["application", "membership", "form"],
        isPublic: true,
        downloadCount: 8,
        lastAccessed: "2024-01-18T11:20:00Z",
      },
      {
        id: "3",
        name: "financial-report-q4-2023.xlsx",
        originalName: "Financial Report Q4 2023.xlsx",
        size: 1024000, // 1MB
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        url: "data:application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;base64,UEsDBBQAAAAIAA==",
        uploadedBy: "board@syndicate.eg",
        uploadedAt: "2024-01-05T16:00:00Z",
        category: "finance",
        description: "Quarterly financial report for Q4 2023",
        tags: ["financial", "report", "q4", "2023"],
        isPublic: false,
        downloadCount: 3,
        lastAccessed: "2024-01-12T10:15:00Z",
      },
    ];

    dispatch({ type: "SET_FILES", payload: sampleFiles });
  };

  // Upload file function
  const uploadFile = async (
    file: File,
    category: string,
    description?: string
  ): Promise<FileRecord> => {
    dispatch({ type: "SET_UPLOADING", payload: true });
    dispatch({ type: "SET_ERROR", payload: null });

    try {
      // Simulate upload progress
      const fileId = Date.now().toString();
      for (let progress = 0; progress <= 100; progress += 10) {
        dispatch({
          type: "SET_UPLOAD_PROGRESS",
          payload: { fileId, progress },
        });
        await new Promise((resolve) => setTimeout(resolve, 100));
      }

      // Convert file to base64 for storage
      const base64 = await new Promise<string>((resolve) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result as string);
        reader.readAsDataURL(file);
      });

      const newFile: FileRecord = {
        id: fileId,
        name: file.name,
        originalName: file.name,
        size: file.size,
        type: file.type,
        url: base64,
        uploadedBy: "admin@syndicate.eg", // In real app, get from auth context
        uploadedAt: new Date().toISOString(),
        category,
        description,
        tags: [],
        isPublic: false,
        downloadCount: 0,
      };

      dispatch({ type: "ADD_FILE", payload: newFile });
      dispatch({ type: "SET_UPLOADING", payload: false });

      return newFile;
    } catch (error) {
      dispatch({ type: "SET_ERROR", payload: "Failed to upload file" });
      dispatch({ type: "SET_UPLOADING", payload: false });
      throw error;
    }
  };

  // Download file function
  const downloadFile = (fileId: string) => {
    const file = state.files.find((f) => f.id === fileId);
    if (!file) return;

    // Increment download count
    dispatch({ type: "INCREMENT_DOWNLOAD", payload: fileId });

    // Create download link
    const link = document.createElement("a");
    link.href = file.url;
    link.download = file.originalName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Delete file function
  const deleteFile = (fileId: string) => {
    dispatch({ type: "DELETE_FILE", payload: fileId });
  };

  // Get files by category
  const getFilesByCategory = (category: string): FileRecord[] => {
    return state.files.filter((f) => f.category === category);
  };

  // Search files
  const searchFiles = (query: string): FileRecord[] => {
    const lowercaseQuery = query.toLowerCase();
    return state.files.filter(
      (f) =>
        f.name.toLowerCase().includes(lowercaseQuery) ||
        f.description?.toLowerCase().includes(lowercaseQuery) ||
        f.tags.some((tag) => tag.toLowerCase().includes(lowercaseQuery))
    );
  };

  // Clear error
  const clearError = () => {
    dispatch({ type: "SET_ERROR", payload: null });
  };

  return (
    <FileContext.Provider
      value={{
        state,
        uploadFile,
        downloadFile,
        deleteFile,
        getFilesByCategory,
        searchFiles,
        clearError,
      }}
    >
      {children}
    </FileContext.Provider>
  );
}

// File hook
export function useFiles() {
  const context = useContext(FileContext);
  if (!context) {
    throw new Error("useFiles must be used within a FileProvider");
  }
  return context;
}
